import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMyWallet,
  useRequestBankChangeVerification,
  useVerifyBankChangeVerification,
  useCreateBankChangeRequest,
  getListBankChangeRequestsQueryKey,
  type BankChangeVerificationStatus,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import {
  Check,
  ChevronLeft,
  Loader2,
  Mail,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { SA_BANKS, branchCodeForBank } from "@/lib/saBanks";

// Surface a server-provided error message (`{ error }` / `{ message }`) when one
// is available, falling back to a generic line otherwise.
function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object") {
      const message =
        (data as { message?: unknown }).message ??
        (data as { error?: unknown }).error;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback;
}

type Phase = "details" | "verify";

/**
 * Multi-step bank-change flow with mandatory dual-channel (SMS + email) OTP
 * verification. The agent enters their new banking details, proves ownership of
 * their registered phone AND email with one-time codes, and only then can the
 * change be submitted. The one-time token returned once both channels are
 * verified is consumed atomically by the create call on the server.
 *
 * Shared by the agent dashboard "Account details" card and the bank-changes
 * screen so the security flow is identical wherever a change is initiated.
 */
export function BankChangeFlowDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: wallet } = useGetMyWallet();

  const [phase, setPhase] = useState<Phase>("details");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [branchCode, setBranchCode] = useState("");

  const [session, setSession] = useState<BankChangeVerificationStatus | null>(
    null,
  );
  const [smsCode, setSmsCode] = useState("");
  const [emailCode, setEmailCode] = useState("");

  const requestVerification = useRequestBankChangeVerification();
  const verifyChannel = useVerifyBankChangeVerification();
  const create = useCreateBankChangeRequest();

  const reset = () => {
    setPhase("details");
    setBankName("");
    setAccountNumber("");
    setAccountType("");
    setBranchCode("");
    setSession(null);
    setSmsCode("");
    setEmailCode("");
  };

  // Reset the whole flow whenever the dialog is closed so a re-open always
  // starts clean (no stale OTP session or half-entered details).
  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const detailsValid = Boolean(
    bankName.trim() &&
    accountNumber.trim() &&
    accountType &&
    branchCode.trim() &&
    wallet,
  );

  const smsVerified = session?.smsVerified ?? false;
  const emailVerified = session?.emailVerified ?? false;
  const token = session?.verificationToken ?? null;

  const sendCodes = async () => {
    try {
      const status = await requestVerification.mutateAsync();
      setSession(status);
      setSmsCode("");
      setEmailCode("");
      setPhase("verify");
    } catch (err) {
      toast({
        title: "Could not send verification codes",
        description: apiErrorMessage(err, "Please try again in a moment."),
        variant: "destructive",
      });
    }
  };

  const verify = async (channel: "sms" | "email", code: string) => {
    if (!session) return;
    try {
      const status = await verifyChannel.mutateAsync({
        id: session.id,
        data: { channel, code },
      });
      setSession(status);
      if (channel === "sms") setSmsCode("");
      else setEmailCode("");
      toast({
        title: channel === "sms" ? "Phone verified" : "Email verified",
        description:
          status.smsVerified && status.emailVerified
            ? "Both channels verified. You can confirm the change."
            : "One more channel to verify.",
      });
    } catch (err) {
      toast({
        title: "Incorrect or expired code",
        description: apiErrorMessage(err, "Check the code and try again."),
        variant: "destructive",
      });
    }
  };

  const confirm = async () => {
    if (!wallet || !token) return;
    try {
      await create.mutateAsync({
        data: {
          agentId: wallet.agentId,
          newBankName: bankName.trim(),
          newAccountNumber: accountNumber.trim(),
          newBranchCode: branchCode.trim(),
          accountType,
          verificationToken: token,
        },
      });
      queryClient.invalidateQueries({
        queryKey: getListBankChangeRequestsQueryKey(),
      });
      toast({
        title: "Bank change submitted",
        description: "Finance will verify your new banking details.",
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Could not submit bank change",
        description: apiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">
            {phase === "details" ? "Update banking details" : "Verify it's you"}
          </DialogTitle>
        </DialogHeader>

        {phase === "details" ? (
          <>
            <p className="text-xs text-gray-500">
              Commission payouts are paused until your new account is verified.
            </p>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Bank name</Label>
                <Select
                  value={bankName}
                  onValueChange={(v) => {
                    setBankName(v);
                    setBranchCode(branchCodeForBank(v));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {SA_BANKS.map((b) => (
                      <SelectItem key={b.name} value={b.name}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Account number</Label>
                <Input
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  className="font-mono"
                  inputMode="numeric"
                  placeholder="1234567890"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Account type</Label>
                <Select value={accountType} onValueChange={setAccountType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cheque">Cheque / Current</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Branch code</Label>
                <Input
                  value={branchCode}
                  readOnly
                  disabled
                  className="font-mono bg-gray-50 text-gray-500"
                  placeholder="Select a bank first"
                />
                <p className="text-xs text-gray-500">
                  Universal branch code — set automatically from your bank.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={sendCodes}
                disabled={!detailsValid || requestVerification.isPending}
                className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
              >
                {requestVerification.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Send verification codes"
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-xl bg-[#0B132B]/5 p-3">
              <ShieldCheck className="w-5 h-5 text-[#E6007E] mt-0.5 shrink-0" />
              <p className="text-sm text-[#0B132B]">
                For your security, confirm both the code we sent by SMS and the
                one sent to your email before we update your account.
              </p>
            </div>

            <div className="space-y-5 py-2">
              <OtpChannel
                icon={<Smartphone className="w-4 h-4" />}
                title="SMS code"
                target={session?.maskedPhone ?? null}
                value={smsCode}
                onChange={setSmsCode}
                verified={smsVerified}
                onVerify={() => verify("sms", smsCode)}
                pending={verifyChannel.isPending}
              />
              <OtpChannel
                icon={<Mail className="w-4 h-4" />}
                title="Email code"
                target={session?.maskedEmail ?? null}
                value={emailCode}
                onChange={setEmailCode}
                verified={emailVerified}
                onVerify={() => verify("email", emailCode)}
                pending={verifyChannel.isPending}
              />
            </div>

            <button
              type="button"
              onClick={sendCodes}
              disabled={requestVerification.isPending}
              className="text-xs font-medium text-[#E6007E] hover:underline disabled:opacity-50 text-left"
            >
              Didn&apos;t get a code? Resend both
            </button>

            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => setPhase("details")}
                disabled={create.isPending}
                className="rounded-xl"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                onClick={confirm}
                disabled={!token || create.isPending}
                className="flex-1 bg-[#00A859] hover:bg-[#008f4c] text-white rounded-xl"
              >
                {create.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1.5" /> Confirm bank change
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OtpChannel({
  icon,
  title,
  target,
  value,
  onChange,
  verified,
  onVerify,
  pending,
}: {
  icon: React.ReactNode;
  title: string;
  target: string | null;
  value: string;
  onChange: (value: string) => void;
  verified: boolean;
  onVerify: () => void;
  pending: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-[#0B132B]">
          <span className="text-gray-400">{icon}</span>
          {title}
          {target ? (
            <span className="text-xs font-normal text-gray-500">
              to {target}
            </span>
          ) : null}
        </div>
        {verified ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#00A859]">
            <Check className="w-3.5 h-3.5" /> Verified
          </span>
        ) : null}
      </div>
      {verified ? null : (
        <div className="flex items-center gap-2">
          <InputOTP
            maxLength={6}
            value={value}
            onChange={onChange}
            aria-label={`${title} verification code`}
            autoComplete="one-time-code"
            inputMode="numeric"
          >
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <Button
            type="button"
            variant="outline"
            onClick={onVerify}
            disabled={value.length !== 6 || pending}
            className="shrink-0"
          >
            {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
          </Button>
        </div>
      )}
    </div>
  );
}
